import { Alert, Button, Grid, Table, Tag, Typography } from "antd";
import { ArrowRightOutlined } from "@ant-design/icons";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import type { components } from "@qorakul/shared/api";
import { api } from "../app/api.ts";
import { SCHOOL_TIME_ZONE, formatSchoolDateTime } from "../app/time.ts";

type AuditEntry = components["schemas"]["AuditEntryDetails"];
type AuditChange = components["schemas"]["AuditChange"];
type Operation = components["schemas"]["AuditOperation"];

const SYSTEM_ACTOR = "system";
const PAGE_SIZE = 10;
const DATE_FORMAT = "DD.MM.YYYY";
const DATE_TIME_FORMAT = "DD.MM.YYYY HH:mm";

/** Whole-string shapes System.Text.Json writes for DateOnly, TimeOnly and DateTimeOffset values. */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const ISO_TIME = /^(\d{2}:\d{2}):\d{2}(\.\d+)?$/;
const ISO_DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})$/;

const operationColor: Record<Operation, string> = { Added: "green", Modified: "blue", Deleted: "red" };

/** Fields whose values are enum codes with an existing translation, whatever the entity. */
const valueKeyPrefix: Record<string, string> = {
  roles: "roles",
  relationship: "family.relationships",
  status: "familyDetail.statuses",
  preferredLanguage: "familyDetail.languages",
  weekday: "academicYears.days",
  weeklyDayOff: "academicYears.days",
  gender: "student.genders",
  option: "student.livingOptions",
  livingOption: "billing.livingOptions",
};

/** Per entity type, fields whose codes mean something else there; checked before valueKeyPrefix (an attendance status is not a family status). */
const entityValueKeyPrefix: Record<string, Record<string, string>> = {
  AcademicYear: { status: "academicYears.statuses" },
  AttendanceRecord: { status: "attendance.statuses" },
  AttendanceAdjustment: { oldStatus: "attendance.statuses", newStatus: "attendance.statuses" },
  TeacherRate: { kind: "courses.kinds", subjectGroup: "subjects.rateGroups" },
};

const valuePrefixFor = (entityType: string, field: string): string | undefined => entityValueKeyPrefix[entityType]?.[field] ?? valueKeyPrefix[field];

interface Props {
  entityType: string;
  entityId: string;
  /** Also show the entries of the entity's children (for a family: its guardians and students). */
  includeChildren?: boolean;
  /** A display name for an entity id (for example a guardian's name); entries fall back to names found in their own values. */
  describe?: (entityId: string) => string | undefined;
}

/** Read-only change history of one entity from GET /audit. Render it only for users holding audit.read. */
export function AuditHistory({ entityType, entityId, includeChildren = false, describe }: Props) {
  const { t } = useTranslation();
  const screens = Grid.useBreakpoint();
  const [page, setPage] = useState(1);

  const query = useQuery({
    queryKey: ["audit", { entityType, entityId, includeChildren, page }],
    queryFn: async () => {
      const { data, error } = await api.GET("/audit", {
        params: { query: { EntityType: entityType, EntityId: entityId, IncludeChildren: includeChildren, Page: page, PageSize: PAGE_SIZE } },
      });
      if (error || !data) throw new Error(t("app.error"));
      return data;
    },
    placeholderData: keepPreviousData,
  });

  function formatScalar(entityType: string, field: string, value: unknown): string {
    if (value === null || value === undefined || value === "") return "—";
    if (typeof value === "boolean") return value ? t("common.yes") : t("common.no");
    if (typeof value === "string") {
      const prefix = valuePrefixFor(entityType, field);
      return prefix ? t(`${prefix}.${value}`, { defaultValue: value }) : formatTemporal(value);
    }
    return typeof value === "number" ? String(value) : JSON.stringify(value);
  }

  function formatValue(entityType: string, field: string, value: unknown): string {
    if (Array.isArray(value)) return value.length === 0 ? "—" : value.map((item) => formatScalar(entityType, field, item)).join(", ");
    return formatScalar(entityType, field, value);
  }

  function fieldLabel(field: string): string {
    return t(`audit.fields.${field}`, { defaultValue: field });
  }

  function entityLabel(entry: AuditEntry): string {
    const type = t(`audit.entityTypes.${entry.entityType}`, { defaultValue: entry.entityType });
    const name = describe?.(entry.entityId) ?? nameFromChanges(entry.changes);
    return name ? `${type}: ${name}` : type;
  }

  function renderChange(change: AuditChange, entry: AuditEntry) {
    const field = change.property;
    const operation = entry.operation;
    // The API names referenced entities (teacher, subject, family, …); unknown or deleted ones keep the raw id.
    // An empty value shows as "—", so an added or deleted entry never leaves a bare label.
    const oldText = change.oldDisplay ?? formatValue(entry.entityType, field, change.old);
    const newText = change.newDisplay ?? formatValue(entry.entityType, field, change.new);
    const showOld = operation !== "Added";
    const showNew = operation !== "Deleted";
    return (
      <div key={field} style={{ lineHeight: 1.5 }}>
        <Typography.Text type="secondary">{fieldLabel(field)}: </Typography.Text>
        {showOld && <Typography.Text delete={showNew && change.old != null}>{oldText}</Typography.Text>}
        {showOld && showNew && <ArrowRightOutlined aria-hidden style={{ marginInline: 6, fontSize: 11 }} />}
        {showNew && <Typography.Text>{newText}</Typography.Text>}
      </div>
    );
  }

  const columns = [
    {
      title: t("audit.columns.time"),
      key: "time",
      width: 140,
      render: (_: unknown, entry: AuditEntry) => formatSchoolDateTime(entry.occurredAt),
    },
    {
      title: t("audit.columns.actor"),
      key: "actor",
      width: 170,
      render: (_: unknown, entry: AuditEntry) => (entry.actorName === SYSTEM_ACTOR && !entry.actorUserId ? <Typography.Text type="secondary">{t("audit.system")}</Typography.Text> : entry.actorName),
    },
    {
      title: t("audit.columns.operation"),
      key: "operation",
      width: 200,
      render: (_: unknown, entry: AuditEntry) => (
        <div style={{ lineHeight: 1.5 }}>
          <Tag color={operationColor[entry.operation]}>{t(`audit.operations.${entry.operation}`)}</Tag>
          {includeChildren && <div>{entityLabel(entry)}</div>}
        </div>
      ),
    },
    {
      title: t("audit.columns.changes"),
      key: "changes",
      render: (_: unknown, entry: AuditEntry) =>
        entry.changes.length === 0 ? <Typography.Text type="secondary">—</Typography.Text> : entry.changes.map((change) => renderChange(change, entry)),
    },
  ];

  return (
    <>
      {query.isError && <Alert type="error" showIcon title={t("app.error")} action={<Button onClick={() => void query.refetch()}>{t("app.retry")}</Button>} />}
      <Table<AuditEntry>
        rowKey="id"
        size={screens.md ? "middle" : "small"}
        loading={query.isPending}
        columns={columns}
        dataSource={query.data?.items ?? []}
        locale={{ emptyText: t("audit.empty") }}
        scroll={{ x: 640 }}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: Number(query.data?.totalCount ?? 0),
          showSizeChanger: false,
          hideOnSinglePage: true,
          onChange: setPage,
        }}
      />
    </>
  );
}

/** Shows a recorded date, time or timestamp the way the rest of the portal does; any other string is returned unchanged. */
function formatTemporal(value: string): string {
  const time = ISO_TIME.exec(value);
  if (time?.[1]) return time[1];
  if (ISO_DATE.test(value)) return formatIfValid(dayjs(value), DATE_FORMAT, value);
  if (ISO_DATE_TIME.test(value)) return formatIfValid(dayjs(value).tz(SCHOOL_TIME_ZONE), DATE_TIME_FORMAT, value);
  return value;
}

function formatIfValid(date: dayjs.Dayjs, format: string, fallback: string): string {
  return date.isValid() ? date.format(format) : fallback;
}

/** A human name recorded in the entry itself, so removed guardians or students still read sensibly. */
function nameFromChanges(changes: AuditChange[]): string | undefined {
  const value = (field: string) => {
    const change = changes.find((c) => c.property === field);
    const v = change?.new ?? change?.old;
    return typeof v === "string" && v.length > 0 ? v : undefined;
  };
  const person = [value("lastName"), value("firstName")].filter(Boolean).join(" ");
  return value("fullName") ?? value("name") ?? value("label") ?? (person || undefined);
}
